/**
 * Check if the current chain is an Abstract/ZKsync network
 * Abstract networks use different gas estimation and should not have manual gasLimit
 */
export function isAbstractNetwork(chainId: number | undefined): boolean {
  if (!chainId) return false;
  return chainId === 11124 || chainId === 2741; // Abstract Testnet or Mainnet
}

/**
 * Check if the current chain is a Sei network
 * Sei networks may need higher gas limits or auto estimation
 */
export function isSeiNetwork(chainId: number | undefined): boolean {
  if (!chainId) return false;
  return chainId === 1328 || chainId === 1329; // Sei Atlantic-2 Testnet or Pacific-1 Mainnet
}

/**
 * Get transaction options based on network type
 * For Abstract networks, return empty object to allow auto gas estimation
 * For Sei networks, multiply gas limit by 3-5x for safety (Sei EVM needs higher limits)
 * For other networks, return the provided gasLimit
 */
export function getTxOptions(chainId: number | undefined, gasLimit?: number): { gasLimit?: number } {
  if (isAbstractNetwork(chainId)) {
    // For Abstract networks, let the network auto-estimate gas
    return {};
  }
  
  if (isSeiNetwork(chainId)) {
    // For Sei networks, use MUCH higher gas limits (4x) due to Sei EVM gas schedule differences
    // Sei wallets often underestimate gas needs, especially for complex contract interactions
    // Reference: https://support.sei.io/hc/en-us/articles/out-of-gas-errors
    if (!gasLimit) {
      // For simple transfers, use a safe default
      return { gasLimit: 1000000 };
    }
    // Multiply by 4x for safety, with minimum of 500k for contract interactions
    const seiGasLimit = Math.max(Math.floor(gasLimit * 4), 500000);
    return { gasLimit: seiGasLimit };
  }
  
  // For other networks, use the provided gasLimit if available
  return gasLimit ? { gasLimit } : {};
}

export function handleGasError(error: Error, chainId?: number) {
  console.error("Gas estimation error:", error);
  
  if (error.message.includes("insufficient funds")) {
    return "Insufficient funds for gas";
  }
  
  if (error.message.toLowerCase().includes("out of gas")) {
    if (isSeiNetwork(chainId)) {
      return "Out of gas on Sei Network. This transaction needs more gas than estimated. Please try again - the app will use a higher gas limit.";
    }
    return "Out of gas. Please try again with a higher gas limit.";
  }
  
  if (isSeiNetwork(chainId)) {
    return "Transaction failed on Sei Network. This may be due to gas estimation. Retrying with higher gas limits...";
  }
  
  return "Error estimating gas. Please try again.";
}

export function formatTransactionLink(hash: string, chainId: number) {
  // Define block explorer URLs for each network
  const explorerUrls: { [key: number]: string } = {
    // BSC Networks
    56: "https://bscscan.com/tx/",
    97: "https://testnet.bscscan.com/tx/",
    // Ethereum Networks
    1: "https://etherscan.io/tx/",
    11155111: "https://sepolia.etherscan.io/tx/",
    // Avalanche Networks
    43114: "https://snowtrace.io/tx/",
    43113: "https://testnet.snowtrace.io/tx/",
  };

  const baseUrl = explorerUrls[chainId] || "https://bscscan.com/tx/"; // Default to BSC mainnet if unknown chain
  return `${baseUrl}${hash}`;
}
